import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, MapPin, Navigation, Clock, CreditCard, Wallet, ShieldCheck, Star, Plane, Repeat, CircleDot, CheckCircle2 } from "lucide-react";

const RIDE_TYPES = [
  { value: "course_simple", label: "Course simple", icon: CircleDot },
  { value: "aller_retour", label: "Aller-retour", icon: Repeat },
  { value: "mise_a_disposition", label: "Mise à disposition", icon: Clock },
  { value: "transfert_aeroport", label: "Transfert aéroport", icon: Plane },
];
const HERO = "https://images.unsplash.com/photo-1592309905620-e5b59f6dcb98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxibGFjayUyMG1lcmNlZGVzJTIwc2VkYW4lMjBsdXh1cnklMjBkcml2ZXJ8ZW58MHx8fHwxNzkwMjgxMTU3fDA&ixlib=rb-4.1.0&q=85";

export default function PublicBooking() {
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({
    client_name: "", client_email: "", client_phone: "",
    pickup: params.get("pickup") || "", dropoff: params.get("dropoff") || "",
    ride_type: params.get("ride_type") || "course_simple", date: "", time: "",
    hours: 2, passengers: "1", flight_number: "", manual_km: "", package_id: "",
    payment_method: "onboard", notes: "",
  });
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => { api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runEstimate = useCallback(async () => {
    if (form.ride_type !== "mise_a_disposition" && !form.package_id && (!form.pickup || !form.dropoff) && !form.manual_km) {
      toast.error("Renseignez le départ et l'arrivée (ou la distance en km)");
      return;
    }
    setEstimating(true);
    try {
      const { data } = await api.post("/estimate", {
        pickup: form.pickup, dropoff: form.dropoff, ride_type: form.ride_type,
        date: form.date, time: form.time, hours: Number(form.hours) || 1,
        manual_km: form.manual_km ? Number(form.manual_km) : null,
        package_id: form.package_id || null,
      });
      setEstimate(data);
      if (data.error) toast.warning(data.error);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setEstimating(false); }
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    if (!estimate || estimate.price == null) { toast.error("Veuillez d'abord estimer le prix"); return; }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        client_name: form.client_name, client_email: form.client_email, client_phone: form.client_phone,
        pickup: form.pickup, dropoff: form.dropoff, ride_type: form.ride_type,
        date: form.date, time: form.time, hours: Number(form.hours) || 1,
        passengers: Number(form.passengers), flight_number: form.flight_number,
        manual_km: form.manual_km ? Number(form.manual_km) : null,
        package_id: form.package_id || null, payment_method: form.payment_method, notes: form.notes,
      });
      if (form.payment_method === "card") {
        const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      setConfirmed(booking);
      toast.success("Réservation confirmée !");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const distanceRide = form.ride_type !== "mise_a_disposition";
  const showPackage = form.ride_type === "transfert_aeroport";

  if (confirmed) {
    return (
      <div className="min-h-screen bg-[#0B0F17] grain">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-16 animate-fade-up">
          <div className="card-surface rounded-2xl p-8 text-center gold-glow" data-testid="booking-confirmation">
            <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-5" />
            <h1 className="font-display text-3xl font-bold text-slate-50">Réservation confirmée</h1>
            <p className="text-slate-400 mt-2">Référence</p>
            <p className="font-mono text-2xl text-[#39FF14] font-semibold mt-1" data-testid="confirmation-reference">{confirmed.reference}</p>
            <div className="mt-6 text-left space-y-3 bg-[#0B0F17] rounded-xl p-5 border border-[#1E293B]">
              <Row label="Trajet" value={`${confirmed.pickup}${confirmed.dropoff ? " → " + confirmed.dropoff : ""}`} />
              <Row label="Date" value={`${confirmed.date} à ${confirmed.time}`} />
              <Row label="Prix" value={`${confirmed.price?.toFixed(2)} €`} gold />
              <Row label="Paiement" value={confirmed.payment_method === "card" ? "Carte bancaire" : "À bord"} />
            </div>
            <p className="text-xs text-slate-500 mt-5 flex items-center justify-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Confirmation envoyée · Rappel automatique la veille
            </p>
            <Button onClick={() => { setConfirmed(null); setEstimate(null); }} className="mt-6 bg-[#39FF14] hover:bg-[#5CFF45] text-[#0B0F17] font-semibold">Nouvelle réservation</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F17] grain">
      <Navbar />
      {/* Hero */}
      <div className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Chauffeur VTC premium" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 sm:pt-24 sm:pb-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#39FF14] mb-4 animate-fade-up">Chauffeur privé · Bordeaux & région</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 max-w-2xl animate-fade-up">
            Votre chauffeur, <span className="text-[#39FF14]">en direct.</span>
          </h1>
          <p className="text-slate-300 mt-5 max-w-xl text-base sm:text-lg animate-fade-up">
            Réservez votre trajet sans intermédiaire. Tarifs transparents, ponctualité garantie, confort premium.
          </p>
          <div className="flex flex-wrap gap-4 mt-7 animate-fade-up">
            {[["Ponctualité", ShieldCheck], ["Tarif transparent", Star], ["Paiement flexible", Wallet]].map(([t, Icon]) => (
              <span key={t} className="flex items-center gap-2 text-sm text-slate-300 bg-[#131926]/70 backdrop-blur border border-[#1E293B] rounded-full px-4 py-2">
                <Icon className="h-4 w-4 text-[#39FF14]" />{t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Booking form */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-20 relative">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <form onSubmit={submit} className="card-surface rounded-2xl p-5 sm:p-8 space-y-6">
              <h2 className="font-display text-2xl font-semibold text-slate-100">Réserver une course</h2>

              {/* Ride type */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#39FF14]/90">Type de course</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" data-testid="booking-ride-type-select">
                  {RIDE_TYPES.map((rt) => {
                    const Icon = rt.icon; const active = form.ride_type === rt.value;
                    return (
                      <button type="button" key={rt.value} data-testid={`ride-type-${rt.value}`}
                        onClick={() => { set("ride_type", rt.value); setEstimate(null); if (rt.value !== "transfert_aeroport") set("package_id", ""); }}
                        className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-all ${active ? "border-[#39FF14] bg-[#39FF14]/10 text-slate-50" : "border-[#1E293B] bg-[#0B0F17] text-slate-400 hover:border-[#334155]"}`}>
                        <Icon className={`h-5 w-5 ${active ? "text-[#39FF14]" : ""}`} />
                        <span className="text-xs font-medium text-center leading-tight">{rt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {showPackage && (
                <div>
                  <Label className="text-slate-300">Forfait aéroport (optionnel)</Label>
                  <Select value={form.package_id} onValueChange={(v) => { set("package_id", v); setEstimate(null); }}>
                    <SelectTrigger className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" data-testid="booking-package-select">
                      <SelectValue placeholder="Choisir un forfait prédéfini" />
                    </SelectTrigger>
                    <SelectContent>
                      {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} — {p.price} €</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Addresses */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#39FF14]" />Départ</Label>
                  <Input value={form.pickup} onChange={(e) => set("pickup", e.target.value)} data-testid="booking-pickup-address-input"
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="12 Cours de l'Intendance, Bordeaux" required />
                </div>
                {form.ride_type !== "mise_a_disposition" && (
                  <div>
                    <Label className="text-slate-300 flex items-center gap-1.5"><Navigation className="h-3.5 w-3.5 text-[#39FF14]" />Arrivée</Label>
                    <Input value={form.dropoff} onChange={(e) => set("dropoff", e.target.value)} data-testid="booking-dropoff-address-input"
                      className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="Aéroport de Bordeaux-Mérignac" />
                  </div>
                )}
              </div>

              {/* Date / time / extras */}
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="booking-date-input"
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" required />
                </div>
                <div>
                  <Label className="text-slate-300">Heure</Label>
                  <Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="booking-time-input"
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" required />
                </div>
                <div>
                  <Label className="text-slate-300">Passagers</Label>
                  <Select value={form.passengers} onValueChange={(v) => set("passengers", v)}>
                    <SelectTrigger className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" data-testid="booking-passengers-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n} passager{n > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                {form.ride_type === "mise_a_disposition" && (
                  <div>
                    <Label className="text-slate-300">Durée (heures)</Label>
                    <Input type="number" min="1" step="0.5" value={form.hours} onChange={(e) => set("hours", e.target.value)} data-testid="booking-hours-count-input"
                      className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" />
                  </div>
                )}
                {form.ride_type === "transfert_aeroport" && (
                  <div>
                    <Label className="text-slate-300">N° de vol (optionnel)</Label>
                    <Input value={form.flight_number} onChange={(e) => set("flight_number", e.target.value)} data-testid="booking-flight-number-input"
                      className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="AF1234" />
                  </div>
                )}
                {distanceRide && !form.package_id && (
                  <div>
                    <Label className="text-slate-300">Distance (km) — si adresse non trouvée</Label>
                    <Input type="number" min="0" step="0.5" value={form.manual_km} onChange={(e) => set("manual_km", e.target.value)} data-testid="booking-manual-km-input"
                      className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="Auto (OpenStreetMap)" />
                  </div>
                )}
              </div>

              {/* Contact */}
              <div className="border-t border-[#1E293B] pt-6 grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Nom</Label>
                  <Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="booking-client-name-input"
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="Jean Dupont" required />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} data-testid="booking-client-email-input"
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="jean@email.fr" required />
                </div>
                <div>
                  <Label className="text-slate-300">Téléphone</Label>
                  <Input value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} data-testid="booking-client-phone-input"
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="+33 6 12 34 56 78" />
                </div>
              </div>

              {/* Payment */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#39FF14]/90">Paiement</Label>
                <RadioGroup value={form.payment_method} onValueChange={(v) => set("payment_method", v)} className="grid sm:grid-cols-2 gap-3 mt-3">
                  <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${form.payment_method === "card" ? "border-[#39FF14] bg-[#39FF14]/10" : "border-[#1E293B] bg-[#0B0F17]"}`}>
                    <RadioGroupItem value="card" data-testid="booking-payment-card-radio" />
                    <CreditCard className="h-5 w-5 text-[#39FF14]" />
                    <div><p className="text-sm font-medium text-slate-100">Carte bancaire</p><p className="text-xs text-slate-500">Paiement sécurisé Stripe</p></div>
                  </label>
                  <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${form.payment_method === "onboard" ? "border-[#39FF14] bg-[#39FF14]/10" : "border-[#1E293B] bg-[#0B0F17]"}`}>
                    <RadioGroupItem value="onboard" data-testid="booking-payment-onboard-radio" />
                    <Wallet className="h-5 w-5 text-[#39FF14]" />
                    <div><p className="text-sm font-medium text-slate-100">Paiement à bord</p><p className="text-xs text-slate-500">Espèces ou TPE</p></div>
                  </label>
                </RadioGroup>
              </div>

              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Précisions (bagages, siège enfant…)" data-testid="booking-notes-input"
                className="bg-[#0B0F17] border-[#1E293B] text-slate-100" />
            </form>
          </div>

          {/* Estimator sticky */}
          <div className="lg:col-span-4">
            <div className="card-surface rounded-2xl p-6 lg:sticky lg:top-24">
              <h3 className="font-display text-lg font-semibold text-slate-100">Estimation du prix</h3>
              <p className="text-sm text-slate-400 mt-1">Distance & durée calculées via OpenStreetMap.</p>
              <Button type="button" onClick={runEstimate} disabled={estimating} data-testid="booking-estimate-price-button"
                className="w-full mt-4 bg-[#1C2436] hover:bg-[#243049] text-slate-100 border border-[#1E293B]">
                {estimating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Estimer le prix"}
              </Button>

              {estimate && estimate.price != null && (
                <div className="mt-5 animate-fade-up">
                  <div className="text-center py-4 border-y border-[#1E293B]">
                    <p className="text-xs uppercase tracking-widest text-slate-500">Prix estimé</p>
                    <p className="font-mono text-4xl font-bold text-[#39FF14] mt-1" data-testid="booking-calculated-price-display">{estimate.price.toFixed(2)} €</p>
                    <div className="flex justify-center gap-4 mt-2 text-xs text-slate-400">
                      {estimate.distance_km != null && <span data-testid="booking-calculated-distance-display">{estimate.distance_km} km</span>}
                      {estimate.duration_min != null && estimate.duration_min > 0 && <span data-testid="booking-calculated-duration-display">~{estimate.duration_min} min</span>}
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5">
                    {estimate.breakdown?.map((b, i) => (
                      <div key={i} className="flex justify-between text-sm"><span className="text-slate-400">{b.label}</span><span className="text-slate-200 font-mono">{b.amount.toFixed(2)} €</span></div>
                    ))}
                  </div>
                </div>
              )}

              <Button type="submit" onClick={submit} disabled={submitting || !estimate?.price} data-testid="booking-submit-button"
                className="w-full mt-6 bg-[#39FF14] hover:bg-[#5CFF45] text-[#0B0F17] font-semibold h-12 text-base">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (form.payment_method === "card" ? "Payer & réserver" : "Confirmer la réservation")}
              </Button>
              <p className="text-xs text-slate-500 mt-3 text-center flex items-center justify-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> Confirmation par email & rappel la veille
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, gold }) => (
  <div className="flex justify-between gap-4 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className={`text-right ${gold ? "text-[#39FF14] font-semibold font-mono" : "text-slate-200"}`}>{value}</span>
  </div>
);
